

import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Pill,
  TableActionTypes,
  Typography,
  useDataTable,
  MUIDataTableMeta
} from 'web-library';
import { ResitSubject } from '../../../api/candidate/resit/model';
import SubjectSelectionLabel from './SubjectSelectionLabel';

const useStyles = makeStyles()((theme) =>
({
  '@global': {
    '#resit-subject-table .MuiTableCell-head .MuiCheckbox-root': {
      display: 'none',
    },
  },
  container: {
    alignItems: 'center',
    paddingBottom: theme.spacing(4),
  },
  selectionLabelContainer: {
    paddingBottom: theme.spacing(4),
  },
  datagridContainer: {
    '& .MuiPaper-root': {
      width: '100%',
    },
  },
}));

export interface SelectSubjectProps {
  selectedSubjects: ResitSubject[];
}

/** to render customer label
 * spread to avoid sonar rules
 * */
const renderCustomLabel = (value: any) => (value ? 'Registered' : 'Not Registered');

/** to check toolbar placement
 * spread to avoid sonar rules
 * */
const checkToolbarPlacement = (isSelected: number) => (isSelected > 0 ? 'replace' : 'none');

const SubjectCode = ({
  rowIndex,
  rows,
  dataIndex,
}: {
  rowIndex: number;
  rows: ResitSubject[];
  dataIndex: number;
}) => <Typography id={`text-subjectCode-row${rowIndex}`}>{rows[dataIndex].subjectCode}</Typography>;

const PillStatus = ({ value }: any) => <Pill label={renderCustomLabel(value)} />;

const SubjectSelectionTable = ({
  onClearSelection,
  hideDescription = false,
  preSelectRows,
  rows,
  onSelectionChange,
}: {
  onClearSelection?: () => void;
  hideDescription?: boolean;
  preSelectRows?: number[];
  rows: ResitSubject[];
  onSelectionChange: (subjects: number[]) => void;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  /** define grid columns
   * */
  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'subject',
      label: t('resit->>RegisterResit.Subject(s)'),
      options: {
        filter: false,
      },
    },
    {
      name: 'subjectCode',
      label: t('resit->>RegisterResit.Subject Code'),
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
          SubjectCode({
            rowIndex,
            rows,
            dataIndex,
          }),
      },
    },
    {
      name: 'examSeries',
      label: t('resit->>RegisterResit.Exam Series'),
      options: {
        filter: false,
      },
    },
    {
      name: 'examLevel',
      label: t('resit->>RegisterResit.Level'),
      options: {
        filter: false,
      },
    },

    {
      name: 'registered',
      label: t('resit->>RegisterResit.Status'),
      options: {
        filter: false,
        customBodyRender: (value: any, tableMeta: MUIDataTableMeta, updateValue: (value: any) => void) => PillStatus({ value }),
      },
    },
  ];

  const { state, dispatch, tOptions, selectionToolbar, remoteTotalRowsSelected } = useDataTable();

  useEffect(() => {
    dispatch({
      type: 'UPDATE_DATA',
      payload: {
        data: rows,
        count: rows.length,
      },
    } as TableActionTypes);
  }, [dispatch, rows]);

  useEffect(() => {
    const convertedSelection: { [key: string]: any } = {};
    if (preSelectRows) {
      preSelectRows.forEach((idx) => {
        convertedSelection[idx] = true;
      });
    }

    dispatch({
      type: 'UPDATE_SELECTION',
      payload: convertedSelection,
    } as TableActionTypes);
  }, [dispatch, preSelectRows]);

  const currentOneRowSelected = useMemo(() => {
    const rowsSelected = Object.keys(state.rowsSelected);
    if (rowsSelected.length === 0) {
      return null;
    }
    return rowsSelected[0];
  }, [state.rowsSelected]);

  /** define grid options
   * */
  const options: MUIDataTableOptions = {
    ...tOptions,
    tableBodyMaxHeight: `calc(100%)`,
    tableBodyHeight: `calc(100%-115px)`,
    selectableRows: 'multiple',
    onRowSelectionChange: (currentRowsSelected: any, allRowsSelected: any, rowsSelected: any) => {
      if (tOptions.onRowSelectionChange) {
        // perform dataTable web-library onChange
        tOptions.onRowSelectionChange(currentRowsSelected, allRowsSelected, rowsSelected);
      }
      onSelectionChange(rowsSelected || []);
    },
    isRowSelectable: (dataInex: number) => {
      const isNotRegistered = Array.isArray(rows) && rows[dataInex] && !rows[dataInex].registered;
      const hasRegistered = Array.isArray(rows) && rows.some((row) => row.registered);

      return (
        !hasRegistered &&
        (currentOneRowSelected === dataInex.toString() ||
          (!currentOneRowSelected && isNotRegistered))
      );
    },
    pagination: true,
    // need to put to make selection replacement work
    selectToolbarPlacement: checkToolbarPlacement(remoteTotalRowsSelected),
    tableId: 'subjectSelection',
  };

  return (
    <Box className={classes.container}>
      {!hideDescription && (
        <Box className={classes.selectionLabelContainer}>
          <SubjectSelectionLabel />
        </Box>
      )}

      <Box id="resit-subject-table">
        <Grid container className={classes.datagridContainer}>
          <MUIDataTable
            // @ts-ignore
            state={state}
            dispatch={dispatch}
            columns={columns}
            data={rows}
            options={options}
            components={{
              TableToolbarSelect: () =>
                selectionToolbar(undefined, () => {
                  if (typeof onClearSelection === 'function') {
                    onClearSelection();
                  }
                }),
            }}
          />
        </Grid>
      </Box>
    </Box>
  );
};

export default SubjectSelectionTable;
